import { useCallback, RefObject } from 'react';

interface UseToolbarActionsProps {
  selectedElement: HTMLElement | null;
  contentRef: RefObject<HTMLDivElement>;
  onContentChange: (content: string) => void;
  setEditingAnchor: (anchor: HTMLAnchorElement | null) => void;
}

export const useToolbarActions = ({ selectedElement, setEditingAnchor }: UseToolbarActionsProps) => {
  const createLink = useCallback(() => {
    if (!selectedElement || selectedElement.contentEditable !== 'true') return null;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    // Create a new anchor element
    const anchor = document.createElement('a');
    anchor.href = '';
    anchor.target = '_blank';
    anchor.style.cssText = 'color: #0972d3; text-decoration: none;';

    if (selectedText && selectedText.trim() !== '') {
      // If text is selected, use it as link text
      anchor.textContent = selectedText;
      range.deleteContents();
    } else {
      // If no text selected, create empty link with placeholder text
      anchor.textContent = 'Link';
    }

    // Insert the anchor
    range.insertNode(anchor);

    // Clear the selection
    selection.removeAllRanges();

    return anchor;
  }, [selectedElement]);

  const applyFormatting = useCallback(
    (command: string) => {
      if (!selectedElement || selectedElement.contentEditable !== 'true') return;

      if (command === 'link') {
        const anchor = createLink();
        if (anchor) {
          setEditingAnchor(anchor);
        }
        return;
      }

      // For other formatting commands
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      switch (command) {
        case 'bold':
          document.execCommand('bold', false);
          break;
        case 'italic':
          document.execCommand('italic', false);
          break;
        case 'underline':
          document.execCommand('underline', false);
          break;
        case 'strikethrough':
          document.execCommand('strikeThrough', false);
          break;
      }

      // Refocus and restore selection
      selectedElement.focus();
    },
    [selectedElement, createLink, setEditingAnchor],
  );

  return {
    applyFormatting,
  };
};
